import clsx from 'clsx'
import type React from 'react'

/**
 * Badge tones on the headless tokens (the design system's
 * code/console/MIGRATION.md, badge.tsx table): `neutral`, the three
 * status colours, and `live` (steel). Catalyst's 18 keys still work, each
 * an alias for one of the five (`BADGE_ALIASES`).
 */
const colors = {
  neutral: 'bg-tint text-fg-2 group-data-hover:bg-wash-3',
  good: 'bg-good-tint text-good group-data-hover:bg-good/20',
  warn: 'bg-warn-tint text-warn group-data-hover:bg-warn/20',
  danger: 'bg-danger-tint text-danger group-data-hover:bg-danger/20',
  live: 'bg-accent-tint-2 text-accent group-data-hover:bg-accent/25',
}

export type BadgeTone = keyof typeof colors

/** Catalyst's colour keys, each folded onto one of the five tones. */
export const BADGE_ALIASES = {
  zinc: 'neutral',
  red: 'danger',
  orange: 'danger',
  rose: 'danger',
  pink: 'danger',
  fuchsia: 'danger',
  amber: 'warn',
  yellow: 'warn',
  lime: 'good',
  green: 'good',
  emerald: 'good',
  teal: 'good',
  cyan: 'live',
  sky: 'live',
  blue: 'live',
  indigo: 'live',
  violet: 'live',
  purple: 'live',
} as const satisfies Record<string, BadgeTone>

export type BadgeColor = BadgeTone | keyof typeof BADGE_ALIASES

function toneOf(color: BadgeColor): BadgeTone {
  return color in BADGE_ALIASES ? BADGE_ALIASES[color as keyof typeof BADGE_ALIASES] : (color as BadgeTone)
}

type BadgeProps = { color?: BadgeColor }

export function Badge({ color = 'neutral', className, ...props }: BadgeProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-sm/5 font-medium sm:text-xs/5 forced-colors:outline',
        colors[toneOf(color)]
      )}
    />
  )
}
